"use client";

import Tooltip from "@/components/design-system/tooltip";
import InfoIcon from "public/next-public/common/icons/info-icon.svg";
import formatPrice from "./formatPrice";
import { ShippingSummary, StandardShipping, getTotalShipping } from "./getShippingSummary";
import styles from "./shippingCost.module.scss";

// "+ $4.99 Standard Shipping ⓘ" and its variants. The tooltip explains what makes up the cost.
export default function ShippingCost({ shipping, vendorName, truncate = false }: ShippingCostProps) {
  const { standard, heavyShipping } = shipping;
  const tooltip =
    heavyShipping !== null
      ? {
          content: <HeavyShippingBreakdown shipping={shipping} heavyShipping={heavyShipping} />,
          className: styles.breakdownTooltip,
        }
      : standard.kind === "included"
        ? { content: <IncludedShippingNote vendorName={vendorName} />, className: styles.includedTooltip }
        : { text: describeStandardShipping(standard, vendorName), className: styles.textTooltip };

  return (
    <span className={`${styles.ShippingCost} ${truncate ? styles.truncate : ""}`}>
      <span className={styles.label}>
        {heavyShipping !== null ? (
          <>
            + <strong>{formatPrice(getTotalShipping(shipping))}</strong> Total Item Shipping
          </>
        ) : (
          <StandardShippingLabel standard={standard} />
        )}
      </span>
      <Tooltip {...tooltip} tailPlacement="above" align="end" anchorInline describeChild>
        <button type="button" className={styles.infoButton} aria-label="Shipping cost details">
          <InfoIcon width={16} height={16} aria-hidden />
        </button>
      </Tooltip>
    </span>
  );
}

// Shown below the price when a heavy-shipping fee applies but isn't known until after the order.
export function UnknownHeavyShippingNote() {
  return (
    <span className={styles.ShippingCost}>
      <span className={styles.note}>*Heavy shipping surcharge will apply</span>
      <Tooltip
        text="Net32 will contact you with the additional shipping surcharge after your order is placed."
        tailPlacement="above"
        align="end"
        anchorInline
        describeChild
      >
        <button type="button" className={styles.infoButton} aria-label="Heavy shipping surcharge details">
          <InfoIcon width={16} height={16} aria-hidden />
        </button>
      </Tooltip>
    </span>
  );
}

function StandardShippingLabel({ standard }: { standard: StandardShipping }) {
  switch (standard.kind) {
    case "cost":
      return (
        <>
          + <strong>{formatPrice(standard.cost)}</strong> Standard Shipping
        </>
      );
    case "free":
      return (
        <>
          & <span className={styles.free}>FREE</span> Standard Shipping
        </>
      );
    case "included":
      return (
        <>
          & <span className={styles.free}>FREE</span> Shipping Included
        </>
      );
  }
}

function describeStandardShipping(standard: StandardShipping, vendorName: string): string {
  if (standard.kind === "cost") {
    return standard.threshold === null
      ? `This is a flat shipping fee from ${vendorName}, regardless of the quantity you buy.`
      : `This is a flat shipping fee from ${vendorName}. If your purchase total across any items with this seller exceeds ${formatPrice(standard.threshold)} then you will qualify for free shipping.`;
  }
  if (standard.kind === "free" && standard.threshold !== null && standard.reason !== "always") {
    return standard.reason === "item"
      ? `This item meets ${vendorName}’s minimum price threshold of ${formatPrice(standard.threshold)} to qualify for free shipping.`
      : `Your items from ${vendorName} meet their minimum price threshold of ${formatPrice(standard.threshold)} to qualify for free shipping.`;
  }
  return `${vendorName} offers free shipping for this item.`;
}

function IncludedShippingNote({ vendorName }: { vendorName: string }) {
  return (
    <>
      There is no <strong>additional</strong> shipping cost for this item because it’s already covered by an item in
      your cart from this same seller ({vendorName}).
    </>
  );
}

function HeavyShippingBreakdown({ shipping, heavyShipping }: { shipping: ShippingSummary; heavyShipping: number }) {
  const { standard } = shipping;
  const included = standard.kind === "included";

  return (
    <div className={styles.breakdown}>
      <dl className={styles.breakdownRows}>
        <div className={styles.breakdownRow}>
          <dt>Standard Shipping{included && "*"}:</dt>
          {standard.kind === "cost" ? (
            <dd>{formatPrice(standard.cost)}</dd>
          ) : (
            <dd className={styles.noCharge}>{included ? "Included" : "FREE"}</dd>
          )}
        </div>
        <div className={styles.breakdownRow}>
          <dt>Heavy Shipping Fee{included ? "**" : "*"}:</dt>
          <dd>{formatPrice(heavyShipping)}</dd>
        </div>
        <div className={`${styles.breakdownRow} ${styles.total}`}>
          <dt>Total:</dt>
          <dd>{formatPrice(getTotalShipping(shipping))}</dd>
        </div>
      </dl>
      {included && (
        <span className={styles.footnote}>
          <strong>*Standard Shipping</strong> has no additional cost because it’s already covered by an item in your
          cart from this same seller.
        </span>
      )}
      <span className={styles.footnote}>
        <strong>{included ? "**" : "*"}Heavy Shipping</strong> increases with item quantity because it is determined by
        weight, box dimensions, package characteristics, and shipping zone.
      </span>
    </div>
  );
}

interface ShippingCostProps {
  shipping: ShippingSummary;
  vendorName: string;
  /** Cut the label short with an ellipsis when it doesn't fit, keeping the info button in view. */
  truncate?: boolean;
}
